import React, { useState, useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import PageInfoButton from '@/components/PageInfoButton';
import {
  Link,
  Plus,
  Search,
  Edit,
  Trash2,
  RefreshCw,
  Building,
  FileText,
  AlertTriangle,
  Calendar,
  User,
  CheckCircle2,
  Clock,
  Archive,
  Wand2,
} from 'lucide-react';
import { notify } from '@core/notifications/notify';
import { useModal, ConfirmModal, FrameworkAssignmentModal, FrameworkAssignmentWizard } from '@components/modal';

interface Framework {
  id: number;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  activeVersionId: number;
  activeVersionName: string;
}

interface Company {
  id: number;
  name: string;
  domain: string;
  industry: string;
  status: 'active' | 'inactive' | 'suspended';
}

interface FrameworkAssignment {
  id: number;
  frameworkId: number;
  frameworkVersionId?: number;
  frameworkVersionName?: string;
  companyId: number;
  companyName: string;
  frameworkName: string;
  assignedBy: number;
  assignedAt: string;
  status: 'pending' | 'active' | 'completed' | 'archived';
  dueDate?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  assignedByName?: string;
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200">
        <CheckCircle2 size={12} /> Active
      </span>
    );
  }
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] bg-amber-100 text-amber-800 border border-amber-200">
        <Clock size={12} /> Pending
      </span>
    );
  }
  if (status === 'completed') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] bg-blue-100 text-blue-800 border border-blue-200">
        <CheckCircle2 size={12} /> Completed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-bold text-[10px] bg-slate-100 text-slate-600 border border-slate-200">
      <Archive size={12} /> Archived
    </span>
  );
}

const FrameworkAssignments: React.FC = () => {
  const { hasPermission, isLoading: authLoading } = useAuth();
  const { openModal } = useModal();
  const [assignments, setAssignments] = useState<FrameworkAssignment[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [frameworks, setFrameworks] = useState<Framework[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCompany, setFilterCompany] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([loadAssignments(), loadCompanies(), loadFrameworks()]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAssignments = async () => {
    try {
      const response = await fetch('/api/compliance/definition/framework-assignments', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) {
        const result = await response.json();
        setAssignments(result.data?.assignments || result.data || []);
      } else {
        notify.error('Failed to load framework assignments');
      }
    } catch (error) {
      notify.error('Failed to load framework assignments');
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await fetch('/api/companies?limit=500&sortBy=createdAt&sortOrder=desc', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) {
        const result = await response.json();
        setCompanies(result.data || []);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const loadFrameworks = async () => {
    try {
      const response = await fetch('/api/compliance/definition/frameworks/assignable', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) {
        const result = await response.json();
        setFrameworks(result.data || []);
      }
    } catch (error) {
      notify.error('Failed to load frameworks');
    }
  };

  const filteredAssignments = assignments.filter(a => {
    const matchesSearch = !searchTerm ||
      a.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.frameworkName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || a.status === filterStatus;
    const matchesCompany = !filterCompany || a.companyId.toString() === filterCompany;
    return matchesSearch && matchesStatus && matchesCompany;
  });

  const handleSaveAssignment = async (assignmentData: Partial<FrameworkAssignment>) => {
    if (!assignmentData.companyId || assignmentData.companyId === 0) {
      notify.error('Please select a company'); return;
    }
    if (!assignmentData.frameworkId || assignmentData.frameworkId === 0) {
      notify.error('Please select a framework'); return;
    }
    const selectedFramework = frameworks.find(f => f.id === assignmentData.frameworkId);
    if (!selectedFramework) {
      notify.error('Selected framework is not available for assignment'); return;
    }

    try {
      const isEdit = !!assignmentData.id;
      const url = isEdit ? `/api/compliance/definition/framework-assignments/${assignmentData.id}` : '/api/compliance/definition/framework-assignments';
      const csrfToken = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        body: JSON.stringify({
          frameworkId: assignmentData.frameworkId,
          companyId: assignmentData.companyId,
          notes: assignmentData.notes || null,
          status: assignmentData.status || 'pending',
        }),
      });

      if (response.ok) {
        notify.success(isEdit ? 'Assignment updated' : 'Framework assigned successfully');
        await loadAssignments();
      } else {
        const errorData = await response.json();
        if (errorData.guidance) {
          const g = errorData.guidance;
          // Show a guidance modal instead of a simple toast
          openModal({
            component: ConfirmModal,
            props: {
              title: 'Assignment Cannot Be Completed',
              message: g.message + (g.steps
                ? '\n\n' + g.steps.map((s: any, i: number) => `${i + 1}. ${s.label}`).join('\n')
                : (g.action ? '\n\n→ ' + g.action : '')),
              confirmText: g.steps ? `Go to Step 1: ${g.steps[0]?.label?.split('(')[0]?.trim()}` : (g.action || 'Fix This'),
              cancelText: 'Close',
              type: 'info',
              onConfirm: () => {
                const link = g.steps ? g.steps[0]?.link : g.link;
                if (link) window.location.href = link;
              },
              onCancel: () => {},
            },
          });
        } else {
          notify.error(errorData.message || 'Failed to save assignment');
        }
      }
    } catch (error) {
      notify.error('Failed to save assignment');
    }
  };

  const handleDeleteAssignment = async (assignmentId: number) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Delete Assignment',
        message: 'Are you sure you want to delete this framework assignment? This action cannot be undone.',
        confirmText: 'Delete', cancelText: 'Cancel', type: 'danger',
        onConfirm: async () => {
          try {
            const csrf = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
            const response = await fetch(`/api/compliance/definition/framework-assignments/${assignmentId}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
            });
            if (response.ok) {
              notify.success('Assignment deleted');
              await loadAssignments();
            } else {
              notify.error('Failed to delete assignment');
            }
          } catch (error) {
            notify.error('Failed to delete assignment');
          }
        },
        onCancel: () => {},
      },
    });
  };

  const openCreateModal = () => {
    openModal({
      component: FrameworkAssignmentModal,
      props: { onSubmit: handleSaveAssignment, selectedAssignment: null },
      options: { size: 'large' }
    });
  };

  const openWizard = () => {
    openModal({
      component: FrameworkAssignmentWizard,
      props: { onComplete: loadData },
      options: { size: 'large' }
    });
  };

  const openEditModal = (assignment: FrameworkAssignment) => {
    openModal({
      component: FrameworkAssignmentModal,
      props: { onSubmit: handleSaveAssignment, selectedAssignment: assignment, companies, frameworks },
      options: { size: 'large' }
    });
  };

  // Derived stats
  const activeCount = assignments.filter(a => a.status === 'active').length;
  const pendingCount = assignments.filter(a => a.status === 'pending').length;

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="text-blue-600 animate-spin" size={40} />
      </div>
    );
  }

  if (!hasPermission('companies.read')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-red-200 p-8 max-w-md">
          <AlertTriangle className="text-red-500 mb-4" size={48} />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">Only Super Admin can access Framework Assignments.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-slate-900 text-left">Framework Assignments</h1>
              <PageInfoButton />
            </div>
            <p className="text-slate-500 text-sm mt-0.5 text-left">Assign compliance frameworks to companies</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadData} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 font-medium text-sm rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
              <RefreshCw size={16} /> Refresh
            </button>
            <button onClick={openWizard} className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white font-medium text-sm rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
              <Wand2 size={16} /> Quick Assign
            </button>
            <button onClick={openCreateModal} className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors shadow-sm">
              <Plus size={16} /> Assign Framework
            </button>
          </div>
        </div>

        {/* Bulk-ops notice — this page is no longer part of the primary flow */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 mb-6">
          <p className="text-xs text-slate-600">
            <strong>Advanced view</strong> — the primary assignment flow is the inline "Assign" modal on the Frameworks page.
            Use this page only for bulk operations.
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Total Assignments</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{assignments.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-emerald-200 p-4 bg-emerald-50/50">
            <p className="text-xs font-medium text-emerald-700 uppercase tracking-wider text-left">Active</p>
            <p className="text-2xl font-bold text-emerald-800 mt-1">{activeCount}</p>
          </div>
          <div className="bg-white rounded-xl border border-amber-200 p-4 bg-amber-50/50">
            <p className="text-xs font-medium text-amber-700 uppercase tracking-wider text-left">Pending</p>
            <p className="text-2xl font-bold text-amber-800 mt-1">{pendingCount}</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-left">Companies</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{companies.length}</p>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search by company or framework name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 text-sm"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </select>
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            className="px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
          >
            <option value="">All Companies</option>
            {companies.map(c => (
              <option key={c.id} value={c.id.toString()}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* Assignments Table */}
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-800">Assignments</h2>
            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">{filteredAssignments.length} result{filteredAssignments.length !== 1 ? 's' : ''}</span>
          </div>

          {filteredAssignments.length === 0 ? (
            <div className="py-12 px-6">
              <Link className="text-slate-300 mb-4" size={40} />
              <h3 className="text-base font-semibold text-slate-700 mb-1">No Assignments Found</h3>
              <p className="text-sm text-slate-500 max-w-sm mb-4">
                {searchTerm || filterStatus || filterCompany
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by assigning your first framework.'}
              </p>
              {!searchTerm && !filterStatus && !filterCompany && (
                <div className="flex items-center justify-center gap-3">
                  <button onClick={openWizard} className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white font-medium text-sm rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
                    <Wand2 size={16} /> Quick Assign Wizard
                  </button>
                  <button onClick={openCreateModal} className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors shadow-sm">
                    <Plus size={16} /> Assign Framework
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead>
                  <tr className="bg-slate-50/50">
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Framework</th>
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Company</th>
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Version</th>
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Assigned</th>
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">By</th>
                    <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredAssignments.map(assignment => (
                    <tr key={assignment.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3 text-left">
                        <p className="text-sm font-semibold text-slate-900">{assignment.frameworkName}</p>
                        {assignment.notes && <p className="text-xs text-slate-500 truncate max-w-[200px]">{assignment.notes}</p>}
                      </td>
                      <td className="px-5 py-3 text-left">
                        <div className="flex items-center gap-2">
                          <Building size={14} className="text-slate-400" />
                          <span className="text-sm text-slate-700">{assignment.companyName}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-left text-sm text-slate-600">
                        {assignment.frameworkVersionName || '-'}
                      </td>
                      <td className="px-5 py-3 text-left">
                        <StatusBadge status={assignment.status} />
                      </td>
                      <td className="px-5 py-3 text-left text-xs text-slate-500 whitespace-nowrap">
                        {new Date(assignment.assignedAt).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-3 text-left text-xs text-slate-500 whitespace-nowrap">
                        {assignment.assignedByName || '-'}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-left">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEditModal(assignment)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                            <Edit size={14} />
                          </button>
                          <button onClick={() => handleDeleteAssignment(assignment.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FrameworkAssignments;
